/**
 * @Relay no-runtime
 */
/**
 * @graphql cc
 */
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from '@apollo/react-hooks';
import graphql from 'graphql-tag';
import type { ApolloError } from 'apollo-client';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';

import type { useUpdateNav4OptInMutation_confluence_ConfluenceMutationApi_updateSiteConfiguration_ConfluenceUpdateSiteConfigurationPayload_errors_MutationError as useUpdateNav4OptInErrorType } from './__types__/useUpdateNav4OptInMutation';
import type {
	useUpdateNav4OptInSitewideMutation as useUpdateNav4OptInSitewideMutationType,
	useUpdateNav4OptInSitewideMutationVariables,
	useUpdateNav4OptInSitewideMutation_confluence_updateNav4OptIn_ConfluenceUpdateNav4OptInPayload_errors_MutationError as useUpdateNav4OptInSitewideErrorType,
} from './__types__/useUpdateNav4OptInSitewideMutation';
import { updateNav4AdminOptInCache } from './nav4AdminOptInCache';

export const useUpdateNav4OptIn = ({
	newIsNav4OptedIn,
	onCompleted,
	onError,
}: {
	newIsNav4OptedIn: boolean;
	onCompleted?: () => void;
	onError?: () => void;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId } = useSessionData();

	const [
		nav4OptInSitewideMutation,
		{ data: optInMutationData, loading: optInMutationLoading, error: optInMutationError },
	] = useMutation<
		useUpdateNav4OptInSitewideMutationType,
		useUpdateNav4OptInSitewideMutationVariables
	>(graphql`
		mutation useUpdateNav4OptInSitewideMutation($cloudId: ID!, $enableNav4: Boolean!) {
			confluence_updateNav4OptIn(cloudId: $cloudId, input: { enableNav4: $enableNav4 }) {
				success
				errors {
					message
				}
			}
		}
	`);

	const updateNav4OptInSitewide = () =>
		nav4OptInSitewideMutation({
			variables: {
				cloudId,
				enableNav4: newIsNav4OptedIn,
			},
		})
			.then((updateSiteConfigData) => {
				const updateSiteConfiguration = updateSiteConfigData?.data?.confluence_updateNav4OptIn;
				if (updateSiteConfiguration?.success) {
					updateNav4AdminOptInCache(newIsNav4OptedIn);
					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							action: 'changed',
							actionSubject: 'navigationVersion',
							source: 'nav4OptIn',
							attributes: {
								originalNavVersion: newIsNav4OptedIn ? '3' : '4',
								newNavVersion: newIsNav4OptedIn ? '4' : '3',
							},
						},
					}).fire();
					onCompleted?.();
				} else {
					// updateSiteConfiguration mutation can return successfully without server errors
					// but have { success: false } in the response. This should be treated as an error
					// as changes were not successfully applied
					handleError(updateSiteConfiguration?.errors);
				}
			})
			.catch((error) => {
				handleError(error);
			});

	const handleError = (
		error:
			| ApolloError
			| useUpdateNav4OptInErrorType[]
			| useUpdateNav4OptInSitewideErrorType[]
			| null
			| undefined,
	) => {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action: 'changeFailed',
				actionSubject: 'navigationVersion',
				source: 'nav4OptIn',
				attributes: {
					error,
				},
			},
		}).fire();

		onError?.();
	};

	return {
		updateNav4OptIn: updateNav4OptInSitewide,
		loading: optInMutationLoading,
		error: optInMutationError,
		isSiteWideUpdateSuccessful: optInMutationData?.confluence_updateNav4OptIn?.success,
	};
};
